/**
 * Wave D — block system commands: define, insert, attributes, visibility
 * states, authoring palettes, plus the attribute/express-tool helpers
 * (TXT2MTXT, BURST, TCIRCLE, TCOUNT) that operate on text/block entities.
 * (docs/architecture/command-parity-plan.md)
 */

import {
    runInteraction, PT, PN, PK, PTxt, PLoopK, requireSelection, entityToPolyline,
} from './lib';
import { undoableAdd, undoableModify, undoableDelete, makeEntity } from '../entityOps';
import { useAppStore, type Entity } from '../../../state/store';
import { useBlockStore, type BlockAttribute } from '../../../state/commandAuxStores';
import type { Pt3 } from '../geometryMath';
import type { RunnerMap } from '../runnerRegistry';
import { notify } from '../../../utils/notify';
import i18n from '../../../i18n';

/** Deep-translate a data bag by a delta (used by INSERT placement). */
function translateData(data: Record<string, any>, d: [number, number, number]): Record<string, any> {
    const shift = (p: number[]): number[] => [p[0] + d[0], p[1] + d[1], (p[2] ?? 0) + d[2]];
    ['start', 'end', 'position', 'center'].forEach((k) => { if (Array.isArray(data[k])) data[k] = shift(data[k] as number[]); });
    ['vertices', 'controlPoints'].forEach((k) => { if (Array.isArray(data[k])) data[k] = (data[k] as number[][]).map(shift); });
    return data;
}

function centroidOf(list: Entity[]): Pt3 {
    const pts: Pt3[] = [];
    list.forEach((e) => pts.push(...(entityToPolyline(e) ?? [])));
    if (pts.length === 0) return [0, 0, 0];
    const c = pts.reduce((acc, p) => [acc[0] + p[0] / pts.length, acc[1] + p[1] / pts.length, acc[2] / pts.length], [0, 0, 0]);
    return c as Pt3;
}

export const blockRunners: RunnerMap = {
    'blk.define': (args) => {
        const name = args?.join(' ')?.trim();
        if (name) return defineBlock(name);
        runInteraction({
            steps: [PTxt('BLOCK: name'), PK('BLOCK: pick objects to bundle', [])],
            complete: (_p, values, picks) => {
                const label = String(values[0] ?? `Block-${Date.now()}`).trim();
                return defineBlockWith(label, picks);
            },
        });
        return 'BLOCK: enter name';
    },

    'blk.insert': (args) => {
        const name = args?.[0];
        const defs = useBlockStore.getState().defs;
        if (!name || !defs[name]) {
            const list = Object.keys(defs);
            return list.length
                ? `INSERT: known blocks: ${list.join(', ')} — usage: INSERT <name>`
                : 'INSERT: no block definitions yet (use BLOCK first).';
        }
        runInteraction({
            steps: [
                PT('INSERT: insertion point'),
                PN('INSERT: scale factor <1>'),
                PN('INSERT: rotation angle (deg) <0>'),
            ],
            complete: (pts, values) => {
                const scale = Number(values[1]) || 1;
                const rot = Number(values[2]) || 0;
                return insertBlock(name, [pts[0].x, pts[0].y, pts[0].z], scale, rot);
            },
        });
        return `INSERT "${name}": specify point, scale, rotation`;
    },

    'blk.write': (args) => {
        const name = args?.[0];
        const def = name ? useBlockStore.getState().defs[name] : null;
        if (!def) return 'WBLOCK: specify an existing block name.';
        const json = JSON.stringify(def, null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${name}.yapblock.json`;
        a.click();
        return `WBLOCK: "${name}" exported (${def.entities.length} component(s)).`;
    },

    'blk.edit': (args) => {
        const name = args?.[0];
        const def = name ? useBlockStore.getState().defs[name] : undefined;
        if (!def) return 'BEDIT: specify a block name.';
        // Open the block components as an editable working set (selected).
        const ids = undoableAdd(def.entities.map((e) => ({ ...e, id: `cad:${crypto.randomUUID()}`, data: structuredClone(e.data), name: `[edit] ${e.name}` })));
        useAppStore.getState().select(ids);
        notify(i18n.t('tools.notify.beditExpanded', { name: name }), 'info');
        return `BEDIT: ${ids.length} component(s) placed for editing.`;
    },

    'blk.attdef': () => {
        runInteraction({
            steps: [PTxt('ATTDEF: tag'), PTxt('ATTDEF: prompt'), PTxt('ATTDEF: default'), PT('ATTDEF: position')],
            complete: (pts, values) => {
                undoableAdd([makeEntity('attdef', `AttDef-${Date.now()}`, {
                    tag: String(values[0] ?? 'TAG'), prompt: String(values[1] ?? ''),
                    defaultValue: String(values[2] ?? ''), position: [pts[0].x, pts[0].y, pts[0].z],
                })]);
                return 'Attribute definition placed (bundle it with BLOCK).';
            },
        });
        return 'ATTDEF: enter tag';
    },

    'blk.attMan': () => {
        const { defs, setAttributes } = useBlockStore.getState();
        const list = Object.values(defs);
        if (list.length === 0) return 'BATTMAN: no block definitions.';
        runInteraction({
            steps: [PTxt('BATTMAN: block'), PTxt('BATTMAN: tag=prompt=default (comma list)')],
            complete: (_p, values) => {
                const name = String(values[0] ?? '');
                if (!defs[name]) return `BATTMAN: no block "${name}".`;
                const attrs: BlockAttribute[] = String(values[1] ?? '')
                    .split(',').map((s) => s.trim()).filter(Boolean)
                    .map((spec) => {
                        const [tag = 'TAG', prompt = '', def = ''] = spec.split('=');
                        return { tag, prompt, defaultValue: def };
                    });
                setAttributes(name, attrs);
                return `BATTMAN: ${attrs.length} attribute(s) set on "${name}".`;
            },
        });
        return 'BATTMAN: block name';
    },

    'blk.attEdit': () => {
        runInteraction({
            steps: [PK('EATTEDIT: pick a block reference'), PTxt('EATTEDIT: Tag=Value pairs (comma)')],
            complete: (_p, values, picks) => {
                const entries: Record<string, string> = {};
                String(values[0] ?? '').split(',').forEach((s) => {
                    const [k = '', v = ''] = s.split('=');
                    if (k.trim()) entries[k.trim()] = v;
                });
                if (Object.keys(entries).length === 0) return 'EATTEDIT: no Tag=Value pairs given.';
                undoableModify(picks, (d) => ({ ...d, attributeValues: { ...(d.attributeValues as object ?? {}), ...entries } }));
                return `EATTEDIT: ${Object.keys(entries).length} attribute value(s) updated.`;
            },
        });
        return 'EATTEDIT: pick a block reference';
    },

    'blk.param': (args) => {
        const run = (name: string) => {
            const kind = (args?.[0] ?? 'Linear').replace(/^./, (c) => c.toUpperCase());
            useBlockStore.getState().addParameter(name, kind);
            return `BPARAMETER: ${kind} parameter added to "${name}".`;
        };
        const { defs } = useBlockStore.getState();
        const list = Object.keys(defs);
        if (list.length === 0) return 'BPARAMETER: define a block first.';
        runInteraction({ steps: [PTxt(`BPARAMETER: block (${list.join('/')})`)], complete: (_p, v) => run(String(v[0] ?? list[0])) });
        return 'BPARAMETER: choose block';
    },

    'blk.action': (args) => {
        const { defs } = useBlockStore.getState();
        const list = Object.keys(defs);
        if (list.length === 0) return 'BACTION: define a block first.';
        runInteraction({
            steps: [PTxt(`BACTION: block (${list.join('/')})`), PTxt('BACTION: action kind (Stretch/Move/Scale)')],
            complete: (_p, v) => {
                const name = String(v[0] ?? list[0]);
                const def = defs[name];
                const param = def?.parameters?.[0]?.id;
                if (!param) return `BACTION: "${name}" has no parameter to bind.`;
                useBlockStore.getState().addAction(name, String(v[1] ?? 'Stretch'), param);
                return `BACTION: action bound to ${param} on "${name}".`;
            },
        });
        return 'BACTION: choose block';
    },

    // BVSTATE: two behaviors — (1) with a selected block insert, set its
    // active visibility state (real render effect via BlockRefMesh);
    // (2) with no selection, register a new state name on a definition.
    'blk.visState': (args) => {
        const { defs, addVisState } = useBlockStore.getState();
        const list = Object.keys(defs);
        if (list.length === 0) return 'BVSTATE: define a block first.';
        const { entities, selectedIds } = useAppStore.getState();
        const insert = selectedIds.find((id) => entities[id]?.type === 'insert');
        if (insert) {
            const e = entities[insert];
            const name = String(e.data.blockName ?? '');
            const states = defs[name]?.visibilityStates ?? [];
            const wanted = args?.join(' ').trim();
            const state = wanted && states.includes(wanted) ? wanted : (states[0] ?? null);
            if (!state) return `BVSTATE: "${name}" has no visibility states (create with BVSTATE <name>).`;
            undoableModify([insert], (d) => ({ ...d, visibilityState: d.visibilityState === state ? '' : state }));
            return `BVSTATE: instance toggled toward "${state}".`;
        }
        runInteraction({
            steps: [PTxt(`BVSTATE: block (${list.join('/')})`), PTxt('BVSTATE: new state name')],
            complete: (_p, v) => {
                const name = String(v[0] ?? list[0]);
                const state = String(v[1] ?? `State-${(defs[name]?.visibilityStates?.length ?? 0) + 1}`).trim();
                addVisState(name, state);
                return `BVSTATE: "${state}" added to "${name}" (tag components with BLOCKVISIBILITYPARAMETERS, then set via BVSTATE on an insert).`;
            },
        });
        return 'BVSTATE: choose block';
    },

    'blk.authorPalette': () => {
        notify(i18n.t('tools.notify.bauthorpaletteDynamicBlockToolsAvailableVia'), 'info');
        return 'Block Authoring Palette: use BPARAMETER, BACTION, BVSTATE.';
    },

    // ------------- text / attribute helpers -------------
    'blk.mleaderText2mtext': () => {
        const list = requireSelection('TXT2MTXT');
        if (!list) return 'TXT2MTXT: select single-line texts first.';
        const texts = list.filter((e) => e.type === 'text' && !e.data.multiline);
        if (texts.length < 2) return 'TXT2MTXT: pick 2+ single-line texts.';
        const joined = texts.map((e) => String((e.data as Record<string, unknown>).content ?? '')).join('\n');
        const first = texts[0];
        const pos = (first.data.position as number[]) ?? [0, 0, 0];
        undoableDelete(texts.map((e) => e.id));
        undoableAdd([makeEntity('mtext', `Merged-${Date.now()}`, {
            content: joined, position: pos, styleId: 'standard', rotation: 0, multiline: true,
        })]);
        return `TXT2MTXT: ${texts.length} texts merged into one.`;
    },

    'blk.burst': () => {
        runInteraction({
            steps: [PK('BURST: pick a block reference')],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const e = entities[picks[0]];
                if (!e || e.type !== 'insert') return 'BURST: pick a block reference.';
                const def = useBlockStore.getState().defs[String((e.data as Record<string, unknown>).blockName ?? '')];
                if (!def) return 'BURST: block definition missing.';
                const p = (e.data.position as number[]) ?? [0, 0, 0];
                const vals = (e.data.attributeValues as Record<string, string>) ?? {};
                const created = def.entities.map((src) => {
                    const clone = { ...src, id: `cad:${crypto.randomUUID()}`, data: translateData(structuredClone(src.data), [p[0] ?? 0, p[1] ?? 0, p[2] ?? 0]) };
                    if (src.type === 'attdef') {
                        const tag = String(src.data.tag ?? '');
                        return makeEntity('text', `Burst-${tag}`, {
                            content: vals[tag] ?? String(src.data.defaultValue ?? tag),
                            position: clone.data.position ?? [0, 0, 0], styleId: 'standard', rotation: 0, multiline: false,
                        });
                    }
                    return clone;
                });
                undoableDelete([picks[0]]);
                undoableAdd(created);
                return `BURST: ${created.length} component(s) exploded, attributes kept as plain text.`;
            },
        });
        return 'BURST: pick a block';
    },

    'blk.tcircle': () => {
        runInteraction({
            steps: [PLoopK('TCIRCLE: pick texts', 1), PTxt('TCIRCLE: shape (Circles/Slots/Rectangles)')],
            complete: (_p, values, picks) => {
                const { entities } = useAppStore.getState();
                const shape = String(values[0] ?? 'Circles');
                let n = 0;
                picks.forEach((id) => {
                    const e = entities[id];
                    if (!e || !['text', 'mtext'].includes(e.type)) return;
                    const p = (e.data.position as number[]) ?? [0, 0, 0];
                    const content = String(e.data.content ?? '');
                    const r = Math.max(0.4, content.length * 0.12);
                    if (shape.startsWith('Rect')) {
                        undoableAdd([makeEntity('rectangle', `TCircle-${Date.now()}`, { corner1: [p[0] - r, p[1] - r * 0.7, p[2] ?? 0], corner2: [p[0] + r, p[1] + r * 0.7, p[2] ?? 0], color: '#ffffff' })]);
                    } else {
                        undoableAdd([makeEntity('circle', `TCircle-${Date.now()}`, { center: [p[0], p[1], p[2] ?? 0], radius: r, color: '#ffffff' })]);
                    }
                    n++;
                });
                return `TCIRCLE: ${n} frame(s) added (${shape}).`;
            },
        });
        return 'TCIRCLE: pick texts';
    },

    'blk.tcount': (args) => {
        const start = Number(args?.[0]) || 1;
        const step = Number(args?.[1]) || 1;
        runInteraction({
            steps: [PLoopK('TCOUNT: pick texts in order', 1)],
            complete: (_p, _v, picks) => {
                let i = start;
                picks.forEach((id) => {
                    undoableModify([id], (d) => { const out = { ...d, content: String(i) }; i += step; return out; });
                });
                return `TCOUNT: ${picks.length} text(s) numbered from ${start}.`;
            },
        });
        return `TCOUNT: numbering from ${start}`;
    },

    'blk.clipit': () => {
        runInteraction({
            steps: [PK('CLIPIT: pick the clip boundary'), PK('CLIPIT: pick the object to clip')],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const boundary = entityToPolyline(entities[picks[0]]);
                if (!boundary || boundary.length < 3) return 'CLIPIT: the boundary is not a closed polygon.';
                undoableModify([picks[1]], (d) => ({ ...d, clipBoundary: boundary }));
                return 'CLIPIT: clip boundary applied (reference contents outside it hide).';
            },
        });
        return 'CLIPIT: pick boundary then object';
    },
};

function defineBlock(name: string): string {
    const list = requireSelection('BLOCK');
    if (!list) return 'BLOCK: select objects first.';
    return defineBlockWith(name, list.map((e) => e.id));
}

function defineBlockWith(name: string, ids: string[]): string {
    const app = useAppStore.getState();
    const entities = ids.map((id) => app.entities[id]).filter(Boolean) as Entity[];
    if (entities.length === 0) return 'BLOCK: no valid objects selected.';
    const c = centroidOf(entities);
    const local = entities.map((e) => ({
        ...e,
        data: translateData(structuredClone(e.data), [-c[0], -c[1], -c[2]]),
    }));
    // The block's own attdefs become its attribute list.
    const attributes: BlockAttribute[] = entities
        .filter((e) => e.type === 'attdef')
        .map((e) => ({ tag: String(e.data.tag ?? ''), prompt: String(e.data.prompt ?? ''), defaultValue: String(e.data.defaultValue ?? '') }));
    useBlockStore.getState().define({ name, entities: local, basePoint: [0, 0, 0], attributes });
    undoableDelete(entities.map((e) => e.id));
    notify(i18n.t('tools.notify.blockDefined', { name: name, count: local.length }), 'success');
    return `BLOCK: "${name}" defined (${local.length} component(s), base at ${c.map((v) => v.toFixed(2)).join(',')}).`;
}

function insertBlock(name: string, at: Pt3, scale = 1, rotationDeg = 0): string {
    const def = useBlockStore.getState().defs[name];
    if (!def) return `INSERT: no block "${name}".`;
    // A single reference entity; EntityLayer renders the definition's
    // components at the insertion point (and BURST/EXPLODEBLOCK expand it).
    // Attribute defaults seed the instance values so they render + edit.
    const attributeValues: Record<string, string> = {};
    def.attributes.forEach((a) => { attributeValues[a.tag] = a.defaultValue; });
    undoableAdd([makeEntity('insert', `${name}-ref`, {
        blockName: name, position: at,
        scale, rotation: rotationDeg,
        attributeValues,
    })]);
    return `INSERT: "${name}" placed at ${at.map((v) => v.toFixed(2)).join(',')} (scale ${scale}, rot ${rotationDeg}°, ${def.entities.length} component(s)).`;
}
